/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {MomentumProgram} from './momentumProgram.model';
import {CommonHelperService} from '../service/common-helper.service';
import {ProgramTiming} from './programTiming';
import {ValidationModel} from './validation.model';

export class KtsTimingTemplateRow extends ValidationModel {

    id: number;
    role: string;
    rowNumber: number;
    version: string;
    fullVersion: string;
    phase: string;
    deliverable: string;
    description: string;
    includeInEsorReport: string;
    milestone: string;
    weeksFromMilestone: number;
    // Gantt Chart data
    duration: number;
    chartLabel: string;
    batchBuildEvent: string;
    // map of grid program key to timing data (PROD: projectName; ECS: PROJECT_ID|VEH_PROJECT_ID|PROJECT_RPO)
    programData = new Map<string, ProgramTiming>();
    within30Days: boolean;
    within60Days: boolean;
    within90Days: boolean;
    status: string;
    type: string;
    // For admin only study mode - allow adjustment of only milestone rows
    studyMode = false;

    fieldRules = [
        {fieldName: 'role', required: false, maxLength: 100},
        {fieldName: 'phase', required: true, maxLength: 50, uniqueFieldSetName: 'ktsRow'},
        {fieldName: 'deliverable', required: true, maxLength: 500, uniqueFieldSetName: 'ktsRow', forErrorMessage: 'Deliverable'},
        {fieldName: 'description', required: false, maxLength: 500, forErrorMessage: 'Description'},
        {fieldName: 'includeInEsorReport', required: false, maxLength: 10, type: 'yesNo'},
        {fieldName: 'milestone', required: true, maxLength: 500, uniqueFieldSetName: 'ktsRow'},
        {fieldName: 'weeksFromMilestone', required: true, minLength: -500, maxLength: 500, type: 'number'},
        {fieldName: 'duration', required: false, minLength: -200, maxLength: 200, type: 'number'},
        {fieldName: 'chartLabel', required: false, maxLength: 500},
        {fieldName: 'batchBuildEvent', required: false, maxLength: 500}
    ];

    constructor(result, useMinorVersion?: boolean) {
        super();
        if (result) {
            this.id = result.id;
            this.rowNumber = result.rowNumber;
            this.role = result.role;
            this.fullVersion = result.version;
            if (useMinorVersion) {
                this.version = result.version;
            } else {
                // remove the trailing minor version from version
                this.version = KtsTimingTemplateRow.getMajorVersionFromFullVersion(result.version);
            }
            this.phase = result.phase;
            this.deliverable = result.deliverable;
            this.description = result.description;
            this.includeInEsorReport = result.includeInEsorReport;
            this.milestone = result.milestone;
            this.weeksFromMilestone = result.weeksFromMilestone;
            this.duration = result.duration;
            this.chartLabel = result.chartLabel;
            this.batchBuildEvent = result.batchBuildEvent;
            this.status = result.status;
            this.type = result.type;
        }
    }

    static getMajorVersionFromFullVersion(version) {
        const normalized = (version ?? '').replace(/\s+(ER\d+.*)$/i, '').trim();
        const versionNumbers = normalized.split('.');
        return versionNumbers[0] + '.' + versionNumbers[1];
    }
    static getMinorVersionFromFullVersion(version) {
        const versionNumbers = version.split('.');
        return versionNumbers[2];
    }

    // ECS templates are plain 5.8.N only (5.8.0, 5.8.1, ...). No Fast/PPTB prefixes,
    // enhancement-release suffixes, or other majors. The timing page also keys ACTIVE
    // rows by major version "5.8", which is allowed as the released-template lookup key.
    static isPlain58Subversion(version: string): boolean {
        return /^5\.8\.\d+$/.test((version ?? '').trim());
    }

    static isAllowedEcsTemplateVersion(version: string): boolean {
        const normalized = (version ?? '').trim();
        return normalized === '5.8' || KtsTimingTemplateRow.isPlain58Subversion(normalized);
    }

    static compareTemplateVersions(left: string, right: string): number {
        const leftParts = KtsTimingTemplateRow.versionParts(left);
        const rightParts = KtsTimingTemplateRow.versionParts(right);
        const length = Math.max(leftParts.length, rightParts.length);
        for (let i = 0; i < length; i++) {
            const delta = (leftParts[i] ?? 0) - (rightParts[i] ?? 0);
            if (delta !== 0) {
                return delta;
            }
        }
        return 0;
    }

    private static versionParts(version: string): number[] {
        return (version ?? '').split('.').map(part => {
            const match = /^(\d+)/.exec(part);
            return match ? parseInt(match[1], 10) : 0;
        });
    }

    private programDataKey(program: MomentumProgram): string {
        return MomentumProgram.getProgramKey(program, this.type);
    }

    calculateDate(momentumPrograms: MomentumProgram[], milestoneMap: Map<any, any>) {
        // Loop through each of the currently selected programs
        for (const momentumProgram of momentumPrograms) {
            // Only calculate dates for rows that match the version of the program
            if (momentumProgram.gvdpVersion !== this.version) {
                continue;
            }
            const dataKey = this.programDataKey(momentumProgram);
            // Get the matching program data from this row
            let program = this.programData.get(dataKey);
            if (!program) {
                // Create a new one if it did not exist
                program = new ProgramTiming();
                this.programData.set(dataKey, program);
            }
            // A manually entered date is a fixed absolute date - never recalculate or overwrite it.
            // Skipping here (before the reset below) preserves program.date and stops the second loop.
            if (program.manualDateEntered) {
                program.needsDateFromSecondLoop = false;
                continue;
            }
            // Default this to true, will be set to false if we find a match
            program.needsDateFromSecondLoop = true;
            // match milestone to deliverable date for milestones
            if (this.phase === 'Milestone') {
                // check our milestone map to ensure it exists
                for (const val of milestoneMap.values()) {
                    // For milestones, the date id should be in the deliverable
                    // IE 'Program Framing Initiation (PFI) - ID # 10853' contains '10853'
                    // the milestoneMap has ['PFI' -> '10853']...
                    if (this.deliverable.includes(val)) {
                        if (this.studyMode) {
                            // the admin page study mode allows milestones to be adjusted
                            program.date = CommonHelperService.calculateDateWithOffset(momentumProgram['date' + val], program.adjustedWeeks);
                        } else {
                            program.date = CommonHelperService.calculateDateWithOffset(momentumProgram['date' + val]);
                        }
                        program.needsDateFromSecondLoop = false;
                        break;
                    }
                }
                if (program.needsDateFromSecondLoop) {
                    console.warn(`Milestone row did not not have a momentum date ID that we recognize: ${this.deliverable}`);
                }
                // if study mode, we must wait for the second loop as we can not be sure the milestones
                // have all been calculated with their adjustments yet
            } else if (!this.studyMode) {
                // if the milestone has a slash, go in order of the specified milestones
                // otherwise, just use the milestone as is
                // Loop through and find the first that exists
                for (const milestone of this.milestone.split(/\s*\/\s*/)) {
                    // If we find at least one matching milestone, no need for second loop
                    if (milestoneMap.get(milestone)) {
                        program.needsDateFromSecondLoop = false;
                        const date = momentumProgram['date' + milestoneMap.get(milestone)];
                        if (date) {
                            // We found the first matching milestone with a date value
                            this.programData.get(dataKey).adjustedWeeks = program.adjustedWeeks;
                            program.date = CommonHelperService.calculateDateWithOffset(date,
                                this.weeksFromMilestone + program.adjustedWeeks);
                            break;
                        }
                    }
                }
            }
        }
    }

    calculateReferenceDate(momentumPrograms: MomentumProgram[], timingTemplateRows: KtsTimingTemplateRow[]) {
        // Loop through each of the currently selected programs
        for (const momentumProgram of momentumPrograms) {
            // Only calculate dates for rows that match the version of the program
            if (momentumProgram.gvdpVersion !== this.version) {
                continue;
            }
            const dataKey = this.programDataKey(momentumProgram);
            // Get the matching program data from this row
            const program = this.programData.get(dataKey);
            if (!program) {
                // something went wrong, this is the second method called, program should always exist
                console.error(`Could not find program data for ${dataKey}`);
                break;
            }
            // If we already have a date, skip to the next program
            if (!program.needsDateFromSecondLoop) {
                continue;
            }
            let referenceRow;
            for (const milestone of this.milestone.split(/\s*\/\s*/)) {
                // Find the timing template row that has deliverable equal to this.milestone and use that date
                // Ignore spacing around :s
                referenceRow = this.findMatchingReferenceRow(timingTemplateRows, milestone);
                if (referenceRow) {
                    // Get the matching program data from the reference row for our current row
                    const refProgram = referenceRow.programData.get(dataKey);
                    if (refProgram.date) {
                        // There's no way to tell if we want to have the adjusted weeks actually drive the calculation
                        // vs just setting it for the user to view. Only Math releases and ICDe Stages are non-milestones that use adjustments
                        if (this.isAdjustable()) {
                            program.date = CommonHelperService.calculateDateWithOffset(refProgram.date + '',
                                this.weeksFromMilestone + program.adjustedWeeks);
                        } else {
                            program.adjustedWeeks = refProgram.adjustedWeeks;
                            program.date = CommonHelperService.calculateDateWithOffset(refProgram.date + '', this.weeksFromMilestone);
                        }
                        // break if we found a matching row that has a date
                        // otherwise, keep looping
                        break;
                    }
                }
            }
            if (!referenceRow) {
                if (this.studyMode) {
                    // we are in study mode and trying to calculate dates based on real milestones that may be adjusted
                    for (const milestone of this.milestone.split(/\s*\/\s*/)) {
                        const milestoneRow = this.findMatchingMilestoneRow(timingTemplateRows, milestone);
                        if (!milestoneRow) {
                            console.warn(`Study Mode: Could not find milestone row for ${this.deliverable} - ${this.milestone} - ${this.version}`);
                            continue;
                        }
                        const programTiming = milestoneRow.programData.get(dataKey);
                        const date = programTiming.date;
                        if (date) {
                            // We found the first matching milestone with a date value
                            this.programData.get(dataKey).adjustedWeeks = programTiming.adjustedWeeks;
                            program.date = CommonHelperService.calculateDateWithOffset(date + '',
                                this.weeksFromMilestone);
                            break;
                        }
                    }
                } else {
                    console.warn(`Could not find reference row for ${this.deliverable} - ${this.milestone} - ${this.version}`);
                }
            }
        }
    }
    // Only Math releases and ICDe Stages are non-milestones that use adjustments
    // admin page allows adjustment of only milestone rows though
    public isAdjustable() {
        if (this.studyMode) {
            return this.phase === 'Milestone';
        }
        return this.deliverable.toUpperCase().includes('MATH RELEASE') ||
            this.deliverable.toUpperCase().includes('ICDE STAGE');
    }

    // True for ICDe Stage deliverables (Stage 0-4), including the (Exterior Lighting) variants. Both the
    // base stage and the Exterior Lighting rows are independently date-editable. Exterior Lighting dates
    // are normally derived from the base stage via calculateReferenceDate (milestone + weeksFromMilestone),
    // but manualDateEntered dates on either row are preserved. Mirrors the isAdjustable() string-match
    // convention so it works during a fresh create (before any saved ProgramRun has populated the
    // icdStageDeliverables set). Note: "ICDe Inline Stage N" does not contain the "ICDe Stage" substring
    // and is intentionally excluded.
    public isIcdeStageRow(): boolean {
        return (this.deliverable || '').toUpperCase().includes('ICDE STAGE');
    }

    // AC3/AC9 inherit predicate: ICDe Stage N and ICDe Inline Stage N, never Exterior Lighting.
    // Do not reuse isIcdeStageRow() — that includes Lighting and excludes Inline.
    public isIcdeInheritFromProdRow(): boolean {
        const deliverable = (this.deliverable || '').toUpperCase();
        if (!deliverable || deliverable.includes('EXTERIOR LIGHTING')) {
            return false;
        }
        return deliverable.includes('ICDE INLINE STAGE') || deliverable.includes('ICDE STAGE');
    }

    public findMatchingReferenceRow(timingTemplateRows: KtsTimingTemplateRow[], milestone: string) {
        const predicate = row => row.deliverable.toUpperCase().replace(/\s*:\s*/g, ' ')
            === milestone.toUpperCase().replace(/\s*:\s*/g, ' ');
        const referenceRow = timingTemplateRows.find(predicate);
        return referenceRow;
    }
    public findMatchingMilestoneRow(timingTemplateRows: KtsTimingTemplateRow[], milestone: string) {
        const predicate = row => row.milestone.toUpperCase().replace(/\s*:\s*/g, ' ')
            === milestone.toUpperCase().replace(/\s*:\s*/g, ' ') && row.phase === 'Milestone';
        const referenceRow = timingTemplateRows.find(predicate);
        return referenceRow;
    }
    setDurationBooleans(today: Date, thirtyDaysFromTodayDate: Date, sixtyDaysFromTodayDate: Date, ninetyDaysFromTodayDate: Date) {
        for (const program of this.programData.values()) {
            const valid = program.date && program.date >= today;
            program.within30Days = valid && program.date <= thirtyDaysFromTodayDate;
            program.within60Days = valid && program.date <= sixtyDaysFromTodayDate;
            program.within90Days = valid && program.date <= ninetyDaysFromTodayDate;
        }
        // Set the row booleans based on the ORing of all the program data booleans
        this.within30Days = Array.from(this.programData.values()).some(p => p.within30Days);
        this.within60Days = Array.from(this.programData.values()).some(p => p.within60Days);
        this.within90Days = Array.from(this.programData.values()).some(p => p.within90Days);
    }
}
